import { useMutation, useQuery } from "@tanstack/react-query";
import { axiosReq } from "@/lib/axios";
import { CampaignStory, CreatePostFormType } from "../types";
import { captureApiError } from "@/lib/sentry-fn";
import posthog from "posthog-js";
import { format } from "date-fns";

export const useCreateCampaignStory = (onGenerate: (val: number, name: string) => void) => {

  const request = async ({ formData, signal }: { formData: CreatePostFormType, signal: AbortSignal }): Promise<{
    data: {
      event_id: number,
      event_name: string,
      stories: CampaignStory[]
    }
  }> => {

    const data = {
      event_description: formData.event_description,
      activity: formData.activity,
      event_time: formData.event_time,
      start_date: format(formData.event_date, 'yyyy-MM-dd'),
      end_date: formData.campiagn_timeline ? format(formData.campiagn_timeline, 'yyyy-MM-dd') : format(formData.event_date, 'yyyy-MM-dd'),
    }


    try {
      const response = await axiosReq.post('/api/approval/create-campaign/', data, { signal });
      return response.data;
    } catch (error) {

      captureApiError(error, {
        component: "useCreateCampaignStory",
        url: `${import.meta.env.VITE_API_URL}/api/approval/create-campaign/`,
        httpMethod: "POST",
        apiEndpoint: "/api/approval/create-campaign"
      })

      throw error
    }
  }

  const { mutate: createSocialCampaign, status, data: generatedCampaignStory } = useMutation({
    mutationFn: (formData: CreatePostFormType) => {
      const controller = new AbortController()
      return request({ formData, signal: controller.signal })
    },
    onSuccess: (data) => {
      console.log("success");

      if (data) {
        onGenerate(data.data.event_id, data.data.event_name)
        posthog.capture('event created', { event_created: data.data.event_id })
      }
    }
  });

  return { createSocialCampaign, status, generatedCampaignStory }

}

export const useGetStoryStatus = (storyId: number, eventId: number, enabled: boolean, image_id?: number) => {

  const request = async (): Promise<{
    success: boolean,
    event_id: number,
    story_id: number,
    image: boolean
  }> => {

    const data = image_id 
    ? { event_id: eventId, story_id: storyId, image_id }
    : {event_id: eventId, story_id: storyId}

    const response = await axiosReq.post('/api/approval/create-story/', data)

    return response.data
  }

  const { data: storyCreationStatus } = useQuery({
    queryKey: ["story-status", storyId],
    queryFn: request,
    enabled: enabled,
    staleTime: 0,
    // gcTime: Infinity
  })

  return { storyCreationStatus }
}

export const useGetSingleCampaign = (event_id: number, image_id?: number) => {

  const request = async (): Promise<{
    data: {
      event_id: number,
      event_name: string,
      stories: CampaignStory[]
    }
  }> => {
    const data = image_id
      ? { event_id, image_id }
      : { event_id }
    const response = await axiosReq.post('/api/approval/get-single-campaign/', data)
    return response.data
  }

  const { data: campaignStories } = useQuery({
    queryKey: ["campaign-stories", event_id],
    queryFn: request
  })

  return { campaignStories }
}

export const useGetCreateStoryImage = (onGenerate: () => void) => {

  const request = async ({ eventId, storyId }: { eventId: number, storyId: number }): Promise<{
    status: string,
  }> => {
    try {
      const response = await axiosReq.post('/api/approval/generate-images-event/', { event_id: eventId, story_id: storyId })
      return response.data
    } catch (error) {

      captureApiError(error, {
        component: "useGetCreatedEventImage",
        url: `${import.meta.env.VITE_API_URL}/api/approval/generate-images-event/`,
        httpMethod: "POST",
        apiEndpoint: "/api/approval/generate-images-event/"
      })

      throw error
    }
  }

  const { data: generatedDataWithImages, status, mutate: generateImage } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      if (data) {
        onGenerate()
      }
    }
  })
  return { generateImage, generatedDataWithImages, status }
}

